import { FORMATTERS } from '../../../common/formats';
import { CLIENT_STATUSES } from '@/common/constants';

export function clientsMapper(response) {
  const results = (response.result || response.data || []).map(
    ({
      _id,
      firstName,
      lastName,
      contactType,
      primaryContact,
      secondaryContact,
      primaryAdvisor,
      readyToSend,
      status,
      belongsToHousehold,
      fileUploadsNo,
      completionPercentage,
      archivedAt,
      secondaryAdvisor,
      interviewStatus,
    }) => {
      // Advisor details
      const {
        id: repId,
        firstName: repFirstName,
        lastName: repLastName,
      } = primaryAdvisor || {};

      // Pick the correct contact object (primary vs secondary)
      const contact =
        contactType === 'Primary' ? primaryContact : secondaryContact;

      // Primary status mapping (including 'Failed')
      let contactStatus = {
        Sent: CLIENT_STATUSES.SENT,
        Draft: CLIENT_STATUSES.DRAFT,
        Ready: CLIENT_STATUSES.READY,
        Complete: CLIENT_STATUSES.CLIENT_COMPLETE,
        PendingReview: CLIENT_STATUSES.PENDING_DOCUSIGN_REVIEW,
        WaitingSignature: CLIENT_STATUSES.WAITING_FOR_SIGNATURE,
        Signed: CLIENT_STATUSES.SIGNED,
        Rejected: CLIENT_STATUSES.REJECTED,
        Archived: CLIENT_STATUSES.ARCHIVED,
        Failed: CLIENT_STATUSES.FAILED,
        Creating: CLIENT_STATUSES.CREATING,
      }[status] || CLIENT_STATUSES.FAILED; // Fallback if status is unrecognized

      // ----- DETERMINE INTERVIEW COMPLETION -------------------------------------------
      const isPrimaryWithEnvelopeComplete =
        contactType === 'Primary' &&
        interviewStatus?.status === 'Envelope Created' &&
        interviewStatus?.percentageCompleted === 100;

      const interviewComplete =
        typeof contact?.interviewComplete === 'boolean'
          ? contact.interviewComplete
          : isPrimaryWithEnvelopeComplete || interviewStatus?.status === 'Complete';

      const legacyPercent = completionPercentage ?? 0;
      let finalCompletionPercent;
      if (interviewComplete) {
        finalCompletionPercent = 100;
      } else if (legacyPercent > 0) {
        finalCompletionPercent = legacyPercent;
      } else if (contactType === 'Primary') {
        // Only the primary contact can fall back to interviewStatus percentage
        finalCompletionPercent = interviewStatus?.percentageCompleted ?? 0;
      } else {
        finalCompletionPercent = 0;
      }

      // Override to ARCHIVED if archivedAt is present
      if (archivedAt) {
        contactStatus = CLIENT_STATUSES.ARCHIVED;
      }
      // If the interview is not complete, override most statuses to SENT
      // except 'Draft', 'Ready', 'Failed', 'Rejected', or 'Archived'
      else if (
        !interviewComplete &&
        !['Draft', 'Ready', 'Failed', 'Rejected', 'Archived', 'Creating'].includes(status)
      ) {
        contactStatus = CLIENT_STATUSES.SENT;
      }

      // isOnboardComplete: raw status Complete/Signed AND interview truly complete
      const isOnboardComplete = ['Complete', 'Signed'].includes(status) && interviewComplete;

      // isInterviewAndDocsComplete: interview done and not in certain DocuSign states
      const isInterviewAndDocsComplete =
        interviewComplete &&
        !['Sent', 'PendingReview', 'WaitingSignature'].includes(status);

      // Construct final client object
      const client = {
        id: _id,
        contactType,
        firstName,
        lastName,

        // Use the chosen contact for familyName, rather than always the primary
        familyName: contact && contact.lastName ? contact.lastName : '',

        repFirstName,
        repLastName,
        repId,
        canSend: readyToSend,
        status: contactStatus,
        isArchived: Boolean(archivedAt),
        signatureRequested: status === 'WaitingSignature',

        // Renamed booleans for clarity
        isOnboardComplete,
        isOnbordComplete: isOnboardComplete, // maintain typo for backward compatibility
        isInterviewAndDocsComplete,

        // Interview progress stats
        interviewCompleted: finalCompletionPercent,
        interviewTotal: 100,
        completionPercent: finalCompletionPercent,

        // Secondary advisor array as provided
        secondaryAdvisor,
        belongsToHousehold,

        // Use .dateTime() safely with optional chaining
        lastContactActivityAt: FORMATTERS.dateTime(contact?.lastActivityAt || ''),

        // File uploads fallback
        uploads: fileUploadsNo || 0,
      };

      return client;
    },
  );

  return {
    results,
    // Prefer `totalResults` but gracefully fall back to `totalCount` (new backend field) and finally
    // to the current page length so pagination keeps working even with partial data.
    totalResults: response.totalResults ?? response.totalCount ?? results.length,
  };
}
